import { useMemo } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Moment } from 'moment-timezone';
import { endpointLink } from '../../index';
import { SparkPoint } from './SensorSparkline';

type RawRecord = { Timestamp: string; jInfo: string };

const withLiveReading = (
  points: SparkPoint[],
  liveValue: number | null | undefined,
  startMs: number,
  endMs: number,
): SparkPoint[] => {
  if (typeof liveValue !== 'number' || Number.isNaN(liveValue)) return points;
  if (!(endMs > startMs)) return points;
  const time = Math.min(Math.max(Date.now(), startMs), endMs);
  const last = points[points.length - 1];
  if (last && time <= last.time) return points;
  return [...points, { time, value: liveValue }];
};

const parseFieldPoints = (data: RawRecord[] | undefined, field: string): SparkPoint[] => {
  if (!data || !field) return [];
  const points: SparkPoint[] = [];
  data.forEach((row) => {
    try {
      const parsed = JSON.parse(row.jInfo || '{}') as Record<string, unknown>;
      const raw = parsed[field];
      if (typeof raw === 'number' && !Number.isNaN(raw)) {
        points.push({ time: new Date(row.Timestamp).getTime(), value: raw });
      }
    } catch {
      // ignore malformed rows
    }
  });
  points.sort((a, b) => a.time - b.time);
  return points;
};

/** Shared raw history fetch — same key for fridge stats + env sparklines. */
export const useEsensorChartRaw = (
  deviceEUI: string,
  startDateTime: Moment,
  endDateTime: Moment,
) => {
  const startIso = startDateTime.toISOString();
  const endIso = endDateTime.toISOString();

  return useQuery<RawRecord[]>({
    queryKey: ['esensorchart', deviceEUI, startIso, endIso],
    queryFn: async () => {
      const response = await axios.get<RawRecord[]>(
        `${endpointLink}esensorchart/${deviceEUI}`,
        { params: { start: startIso, end: endIso } },
      );
      return response.data;
    },
    enabled: !!deviceEUI,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
};

/** Lightweight sparkline series for any jInfo numeric field. */
export const useSensorSparkline = (
  deviceEUI: string,
  field: string,
  startDateTime: Moment,
  endDateTime: Moment,
  liveValue?: number | null,
) => {
  const startMs = startDateTime.valueOf();
  const endMs = endDateTime.valueOf();
  const query = useEsensorChartRaw(deviceEUI, startDateTime, endDateTime);

  const points = useMemo<SparkPoint[]>(() => {
    if (!query.data) return [];
    return withLiveReading(parseFieldPoints(query.data, field), liveValue, startMs, endMs);
  }, [query.data, field, liveValue, startMs, endMs]);

  return {
    ...query,
    points,
  };
};
